import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/Button';
import { Search, User, Menu, LogOut, X } from 'lucide-react';
import { getCurrentUser, logout } from '../authUtils';
import smartlightLogo from '../assets/smartlight-logo-transparent.png';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/DropdownMenu';
import { PAGE_TITLES } from '../hooks/usePageTitle';
import { WorkflowStatusBar } from './WorkflowStatusBar';
import { CarrierEngagementHeader } from './carrier-engagement/CarrierEngagementHeader';
import { CarrierEngagementFilterPills } from './carrier-engagement/CarrierEngagementFilterPills';
import { cn } from '../lib/utils';

function TopBar({
  onMenuOpen,
  isSearchOpen,
  setIsSearchOpen,
  searchDraft,
  setSearchDraft,
  searchText,
  onSearchSubmit,
  onSearchClear,
}) {
  const location = useLocation();
  const user = getCurrentUser();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    if (!isSearchOpen) return;
    const handleMouseDown = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        if (searchDraft === '') {
          setIsSearchOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isSearchOpen, setIsSearchOpen, searchDraft]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
  };

  const pageTitle = PAGE_TITLES[location.pathname] || 'SmartLight';
  const isReferralsPage = location.pathname === '/referrals' || location.pathname === '/closed-referrals';
  const isCarrierEngagementDashboard = location.pathname === '/carrier-engagement-dashboard';

  return (
    <header
      className={cn(
        'flex items-start justify-between bg-background px-4',
        isCarrierEngagementDashboard ? 'h-[104px]' : 'h-[88px]'
      )}
    >
      {/* Left: Hamburger + Logo + Page Title */}
      <div className="flex items-start mt-[20px] gap-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuOpen}
          aria-label="Open navigation menu"
        >
          <Menu className="h-7 w-7 scale-y-[0.8]" strokeWidth={2.5} />
        </Button>

        <div className="flex items-end gap-2">
          <img
            src={smartlightLogo}
            alt="SmartLight Analytics"
            className="h-10 w-auto"
          />

          <div className="h-5 w-[2px] mb-[10px] bg-title/60" />

          <h2 className="text-sm font-bold leading-none tracking-tight mb-[9px] text-title">{pageTitle}</h2>
        </div>
      </div>

      {/* Center: route-specific content. CE dashboard centers its filter
          pills + chevron bar + action buttons between the logo and the
          search/user-menu. Every other page falls back to WorkflowStatusBar. */}
      {isCarrierEngagementDashboard ? (
        <div className="flex-1 flex justify-center items-center h-[104px] gap-6">
          <CarrierEngagementFilterPills />
          <CarrierEngagementHeader />
        </div>
      ) : (
        <div className="flex-1 flex justify-center items-start pt-[20px]">
          <WorkflowStatusBar />
        </div>
      )}

      {/* Right: Actions & User Menu */}
      <div className="flex items-center shrink-0 pt-5 gap-4">
        {/* Search */}
        {(isReferralsPage || isCarrierEngagementDashboard) && (
          <div ref={searchContainerRef} className="relative flex items-center">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Search"
              data-testid="referral-search-button"
              onClick={() => {
                setSearchDraft(searchText);
                setIsSearchOpen(true);
              }}
              className={isSearchOpen ? 'invisible' : undefined}
            >
              <Search className="h-5 w-5" />
            </Button>
            {isSearchOpen && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center">
                <input
                  autoFocus
                  type="text"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSearchSubmit();
                    if (e.key === 'Escape') onSearchClear();
                  }}
                  placeholder="Search Referral ID or Name"
                  data-testid="referral-search-input"
                  className="h-8 pl-3 pr-14 text-xs rounded border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                />
                <button
                  type="button"
                  onClick={onSearchSubmit}
                  aria-label="Submit search"
                  data-testid="referral-search-submit"
                  className="absolute right-7 flex items-center justify-center p-1 rounded transition-colors hover:bg-muted-foreground/10 text-muted-foreground"
                >
                  <Search className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={onSearchClear}
                  aria-label="Clear search"
                  data-testid="referral-search-clear"
                  className="absolute right-1 flex items-center justify-center p-1 rounded transition-colors hover:bg-muted-foreground/10 text-muted-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={() => setUserMenuOpen(!userMenuOpen)}>
            <Button variant="ghost" size="icon" aria-label="User menu">
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent open={userMenuOpen} onClose={() => setUserMenuOpen(false)} align="end" className="min-w-[18rem] p-0">
            <div className="flex items-center gap-3 px-4 py-4">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-foreground whitespace-nowrap">{user?.email || 'User'}</p>
              </div>
            </div>
            <div className="border-t border-border/60" />
            <div className="p-1.5">
              <DropdownMenuItem onClick={handleLogout} className="gap-2.5 text-foreground rounded-md px-3 py-2">
                <LogOut className="h-4 w-4 text-muted-foreground" />
                Sign Out
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export { TopBar };
